import { useContext, useMemo } from 'react'
import { useIntl, type IntlShape } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import BodyText from 'LuxKit/Typography/BodyText'
import PriceDetails from 'tripPlanner/components/ItemOfferPrice/PriceDetails'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import determineOfferFeatureSymbol from 'lib/offer/determineOfferFeatureSymbol'
import { useAppSelector } from 'hooks/reduxHooks'
import { getInclusionsViewableLuxPlusTier } from 'selectors/luxPlusSelectors'
import AddHotelToTripButton from 'tripPlanner/components/AddToTrip/AddHotelToTripButton'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import type {
  HotelRoomOptionInclusion,
  HotelRoomOptionsRate,
  HotelRoomOptionsResult,
  TripItineraryData,
} from 'tripPlanner/api/types'
import type { HotelRoomOption } from 'tripPlanner/types/hotelRoomOption'
import CurrencyContext from 'contexts/currencyContext'
import ChatHotelRoomOptionCancellationPolicy from './ChatHotelRoomOptionCancellationPolicy'

const RateRow = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.L};
  padding: ${rem(14)};
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`

const RateTopLine = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
`

const RateDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(6)};
`

const PackageMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
`

const InclusionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
`

const PriceBlock = styled.div`
  min-width: 0;
  justify-self: start;

  @container (min-width: ${rem(520)}) {
    min-width: ${rem(176)};
  }
`

const RateFooter = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(12)};
  align-items: start;

  @container (min-width: ${rem(520)}) {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: end;
  }
`

const RateFooterAction = styled.div`
  justify-self: start;

  @container (min-width: ${rem(520)}) {
    justify-self: end;
  }
`

function getPriceLabel(
  nights: number | undefined,
  pricingMode: HotelRoomOptionsResult['pricingMode'],
  intl: IntlShape,
): string {
  if (nights != null) {
    return intl.formatMessage({
      id: 'pDdaHwY_41NbMkskomHR',
      defaultMessage: '{count, plural, one {# night} other {# nights}} from',
      description: 'Hotel room option pricing caption showing number of nights in trip planner chat',
    }, { count: nights })
  }

  if (pricingMode === 'exact_dates') {
    return intl.formatMessage({
      id: 'igpA1yeOHDInsmyjU2pu',
      defaultMessage: 'Live total from',
      description: 'Exact-date live price caption in trip planner chat hotel room options',
    })
  }

  return intl.formatMessage({
    id: 'bkopEEiSPXkUwWIWajXd',
    defaultMessage: 'Current from price',
    description: 'Duration-only current price caption in trip planner chat hotel room options',
  })
}

function mapRoomOptionInclusion(
  inclusion: HotelRoomOptionInclusion,
  index: number,
): App.PackageInclusion {
  return {
    id: `${inclusion.text}-${index}`,
    description: inclusion.text,
    symbol: determineOfferFeatureSymbol(inclusion.icon ?? inclusion.text),
    isHighlighted: inclusion.isHighlighted,
    isBonus: inclusion.isBonus,
  }
}

function RatePriceContent({
  rate,
  price,
  currencyCode,
  pricingMode,
  nights,
}: {
  rate: HotelRoomOptionsRate
  price?: number
  currencyCode: string
  pricingMode: HotelRoomOptionsResult['pricingMode']
  nights?: number
}) {
  const intl = useIntl()

  if (price == null) {
    return (
      <BodyText variant="medium" weight="bold">
        {intl.formatMessage({
          id: 'AuqQy3eGXWgQkUu4hmmd',
          defaultMessage: 'Price unavailable',
          description: 'Unavailable price label in trip planner chat hotel room options',
        })}
      </BodyText>
    )
  }

  const value = rate.valuedUpToPrice ?? undefined
  const discount = value && price && value > price ? getValueOffPercent(value, price) : undefined

  return (
    <PriceDetails
      price={price}
      luxPlusPrice={rate.lowestLuxPlusMemberPrice}
      currencyCode={currencyCode}
      priceLabel={getPriceLabel(nights, pricingMode, intl)}
      totalLabel="total"
      value={value}
      discount={discount}
      discountDisplayType="following"
      showTaxesAndFees={rate.amountIncludes === 'all_in'}
    />
  )
}

interface Props {
  rate: HotelRoomOptionsRate
  room: HotelRoomOptionsResult['roomOptions'][number]
  result: HotelRoomOptionsResult
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  currencyCode?: string
  pricingMode: HotelRoomOptionsResult['pricingMode']
  nights?: number
  checkIn?: string
  isRoomOptionSelected?: boolean
}

function ChatHotelRoomOptionsRateOption({
  rate,
  room,
  result,
  itineraryData,
  onSendMessage,
  currencyCode,
  pricingMode,
  nights,
  checkIn,
  isRoomOptionSelected = false,
}: Props) {
  const intl = useIntl()
  const currentCurrency = useContext(CurrencyContext)
  const viewableLuxPlusTier = useAppSelector(getInclusionsViewableLuxPlusTier)
  const title = rate.packageName ?? rate.rateName ?? intl.formatMessage({
    id: 'KeNDhBSRgXPfvaRmwiOr',
    defaultMessage: 'Package option',
    description: 'Fallback package title in trip planner chat hotel room options',
  })
  const inclusions = useMemo(
    () => rate.inclusions?.map(mapRoomOptionInclusion) ?? [],
    [rate.inclusions],
  )
  const luxPlusInclusions = useMemo(
    () => rate.luxPlusInclusions
      ?.filter((inclusion) => inclusion.luxPlusTier === viewableLuxPlusTier)
      .map(mapRoomOptionInclusion) ?? [],
    [rate.luxPlusInclusions, viewableLuxPlusTier],
  )
  const roomOption = useMemo<HotelRoomOption>(() => ({
    packageId: rate.packageId,
    packageName: rate.packageName,
    durationInDays: result.nights,
    roomTypeId: room.roomTypeId,
    roomTypeName: room.roomName,
    rateId: rate.roomRateId,
    checkIn: result.checkIn,
    checkOut: result.checkOut,
    occupancies: result.rooms,
  }), [rate.packageId, rate.packageName, rate.roomRateId, result.checkIn, result.checkOut, result.nights, result.rooms, room.roomName, room.roomTypeId])

  return (
    <RateRow>
      <RateTopLine>
        <PackageMeta>
          <BodyText variant="medium" weight="bold">{title}</BodyText>
          {rate.rateName && rate.rateName !== title && (
            <BodyText variant="small" colour="neutral-three">{rate.rateName}</BodyText>
          )}
          <ChatHotelRoomOptionCancellationPolicy
            cancellationPolicy={rate.cancellationPolicy}
            checkIn={checkIn}
          />
        </PackageMeta>
      </RateTopLine>
      <RateDetails>
        {luxPlusInclusions.length > 0 && (
          <LuxPlusInclusions
            luxPlusInclusions={luxPlusInclusions}
            hideUpsellModal
          />
        )}
        {inclusions.length > 0 && (
          <InclusionList>
            {inclusions.map((inclusion) => (
              <OfferInclusionItem
                key={inclusion.id}
                inclusion={inclusion}
                size="small"
              />
            ))}
          </InclusionList>
        )}
      </RateDetails>
      <RateFooter>
        <PriceBlock>
          <RatePriceContent
            rate={rate}
            price={rate.lowestNonMemberPrice ?? undefined}
            currencyCode={currencyCode ?? result.currencyCode ?? currentCurrency}
            pricingMode={pricingMode}
            nights={nights}
          />
        </PriceBlock>
        <RateFooterAction>
          {onSendMessage && (
            <AddHotelToTripButton
              itineraryData={itineraryData}
              hotelName={result.hotelName}
              offerId={result.offerId}
              onSendMessage={onSendMessage}
              filterNights={result.nights}
              variant="outline"
              size="small"
              roomOption={roomOption}
              isRoomOptionSelected={isRoomOptionSelected}
            />
          )}
        </RateFooterAction>
      </RateFooter>
    </RateRow>
  )
}

export default ChatHotelRoomOptionsRateOption
